import { modelTitleState } from '@/components/canvas/utils/modelTitle'

import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react'
import { type NodeProps, type Node, type Edge, useReactFlow, useNodeConnections, useNodesData } from '@xyflow/react'
import { useParams } from '@tanstack/react-router'
import { TextNode } from '@/components/canvas/nodes'
import type { GenieNodeState } from '@genieartinc/schemas'
import type { CreatedItem, NodeOrchestratorState } from '@genieartinc/schemas'
import { useAIGeneration } from '@/hooks/useAIGeneration'
import { useJobGeneration } from '@/hooks/useJobGeneration'
import { useJobStore } from '@/stores/jobStore'
import { getInputNode } from '@/lib/canvas/utils/getInputNodeData'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import type { HandleMeta } from '@/lib/canvas/types'
import { PURPOSE_HANDLES } from '@/lib/canvas/configs/handles'
import { toast } from '@/components/ui/toast'
import { useNodeModels, getDefaultModel, findModelById } from '@/hooks/useNodeModels'
import { useWalletBalance } from '@/hooks/useWalletBalance'
import { usePinnedParameters } from '@/components/canvas/nodes/pinned-parameters/usePinnedParameters'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { isApiError } from '@/lib/api/api-helpers'
import { useInsufficientBalanceStore } from '@/components/canvas/nodes/base/insufficient-balance-store'
import { useJobEstimate } from '@/hooks/useJobEstimate'
import { streamingStore } from '@/components/canvas/nodes/base/streaming-store'

/**
 * TextNodeNode - React Flow custom node
 *
 * Replaces TextNodeAdapter for the React Flow migration.
 * Wraps the existing TextNode variant component with React Flow handles.
 */

export interface TextNodeNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

/** Default handles from centralized config */
const DEFAULT_TEXT_HANDLES: HandleMeta[] = PURPOSE_HANDLES.TextNode.default

function TextNodeNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as TextNodeNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)
  const { id: canvasIdParam } = useParams({ strict: false })
  const canvasId = canvasIdParam as string | undefined

  const reactFlowInstance = useReactFlow()
  const { generateTextStream, loading, error } = useAIGeneration()
  const { submitJob } = useJobGeneration()
  const updateJobStatus = useJobStore((s) => s.updateJobStatus)
  const { data: textModels } = useNodeModels('text-llm', undefined, 'create')
  const { balance: walletBalance } = useWalletBalance()

  const currentPromptRef = useRef(genieState.prompt || '')
  const isGeneratingRef = useRef(false)
  const mountedRef = useRef(true)
  const generationAbortRef = useRef<AbortController | null>(null)
  // genieState ref to prevent handleGenerate recreation on state changes
  const genieStateRef = useRef(genieState)
  genieStateRef.current = genieState
  const walletBalanceRef = useRef(walletBalance)
  walletBalanceRef.current = walletBalance ?? null

  // Cleanup on unmount: abort in-flight generation + reset refs
  useEffect(() => {
    return () => {
      mountedRef.current = false
      generationAbortRef.current?.abort()
    }
  }, [])

  const [orchestratorState, setOrchestratorState] = useState<NodeOrchestratorState>({
    status: 'idle',
  })

  useEffect(() => {
    if (orchestratorState.status === 'success') {
      const timer = setTimeout(() => {
        setOrchestratorState({ status: 'idle' })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [orchestratorState.status])

  // Sync prompt ref when genieState changes externally
  useEffect(() => {
    currentPromptRef.current = genieState.prompt || ''
  }, [genieState.prompt])

  // Reactively read connected prompt from upstream text node
  // Uses surgical hooks instead of scanning all nodes/edges
  const incomingConnections = useNodeConnections({ handleType: 'target' })

  const sourceNodeIds = useMemo(
    () => incomingConnections.map(c => c.source),
    [incomingConnections],
  )

  const sourceNodesData = useNodesData(sourceNodeIds)

  const connectedPrompt = useMemo(() => {
    if (sourceNodesData.length === 0) return null

    for (const nodeEntry of sourceNodesData) {
      if (!nodeEntry?.data) continue
      const sourceData = nodeEntry.data as Record<string, unknown>
      const sourceState = sourceData?.state as Record<string, unknown> | undefined
      if (!sourceState) continue

      const sourceType = nodeEntry.type || ''
      if (sourceType === 'TextFile' || sourceType === 'TextNode') {
        const createdItems = sourceState.createdItems as Array<{ type: string; content: string }> | undefined
        const latestText = createdItems?.[createdItems.length - 1]
        if (latestText?.type === 'text' && typeof latestText.content === 'string' && latestText.content.trim()) {
          return latestText.content
        }
        const prompt = sourceState.prompt
        if (typeof prompt === 'string' && prompt.trim()) return prompt
      }
    }
    return null
  }, [sourceNodesData])

  const handleChange = useCallback((dataOrUpdater: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => {
    if (typeof dataOrUpdater === 'function') {
      onStateChange((prev) => {
        const result = dataOrUpdater(prev)
        if (result.prompt !== undefined) {
          currentPromptRef.current = result.prompt as string
        }
        if (result.orchestrator !== undefined) {
          setOrchestratorState(result.orchestrator as NodeOrchestratorState)
          const { orchestrator, ...rest } = result
          return rest
        }
        return result
      })
      return
    }

    const d = dataOrUpdater
    if (d.prompt !== undefined) {
      currentPromptRef.current = d.prompt as string
      onStateChange(prev => ({ ...prev, prompt: d.prompt }))
    }
    if (d.title !== undefined) {
      onStateChange(prev => ({ ...prev, title: d.title, titleSource: 'custom', data: { ...(prev.data as Record<string, unknown> || {}), title: d.title } }))
    }
    if (d.images !== undefined) onStateChange(prev => ({ ...prev, images: d.images }))
    if (d.createdItems !== undefined) onStateChange(prev => ({ ...prev, createdItems: d.createdItems }))
    if (d.collapsed !== undefined) onStateChange(prev => ({ ...prev, collapsed: d.collapsed }))
    if (d.orchestrator !== undefined) setOrchestratorState(d.orchestrator as NodeOrchestratorState)
  }, [onStateChange])

  // Selection handled by React Flow — empty intentionally
  const handleSelect = useCallback(() => {}, [])

  // Resolve active model for cost estimation (must be declared before handleGenerate which references costEstimate)
  const defaultTextModel = useMemo(() => getDefaultModel(textModels), [textModels])
  const activeTextModel = useMemo(() => {
    const savedModelId = typeof genieState.modelId === 'string' ? genieState.modelId : undefined
    const savedModelValid = savedModelId && textModels.some(m => m.id === savedModelId)
    const resolvedId = (savedModelValid ? savedModelId : undefined) || defaultTextModel?.id
    if (!resolvedId) return defaultTextModel
    return findModelById(textModels, resolvedId) || defaultTextModel
  }, [textModels, genieState.modelId, defaultTextModel])

  // Cost estimate for insufficient balance dialog
  const { estimate: costEstimate } = useJobEstimate(activeTextModel?.provider ?? null, activeTextModel?.id ?? null, genieState.settings as Record<string, unknown> | undefined)
  const costEstimateRef = useRef(costEstimate)
  costEstimateRef.current = costEstimate

  // Pre-flight balance check helper
  const showInsufficientBalance = useInsufficientBalanceStore((s) => s.showInsufficientBalance)

  const handleGenerate = useCallback(async () => {
    if (isGeneratingRef.current) return
    isGeneratingRef.current = true

    // Abort any previous in-flight generation
    generationAbortRef.current?.abort()
    generationAbortRef.current = new AbortController()

    const targetNodeId = id

    try {
      const genieWindow = window as Window & {
        __genieCanvasStateRef?: { getNodes: () => Node[]; getEdges: () => Edge[] }
      }
      const canvasState = genieWindow.__genieCanvasStateRef
      const inputData = canvasState
        ? getInputNode(id, canvasState.getNodes(), canvasState.getEdges())
        : {}

      const inputPrompt = Array.isArray(inputData.prompt) ? inputData.prompt[0] : inputData.prompt
      const finalPrompt = inputPrompt || connectedPrompt || currentPromptRef.current
      const connectedSystemPrompt = Array.isArray(inputData.systemPrompt) ? inputData.systemPrompt[0] : inputData.systemPrompt
      const bridgeSettings = useCanvasBridgeStore.getState().settingsApi?.getSettings()
      const panelSystemPrompt = bridgeSettings?.systemPrompt
      const currentGenieState = genieStateRef.current
      const finalSystemPrompt = connectedSystemPrompt || panelSystemPrompt || currentGenieState.systemPrompt || ''
      const finalImage = Array.isArray(inputData.image) ? inputData.image[0] : inputData.image

      const promptString = typeof finalPrompt === 'string' ? finalPrompt : ''
      if (!promptString?.trim()) {
        if (mountedRef.current) setOrchestratorState({ status: 'error', message: 'Please enter a prompt or connect a text node' })
        return
      }

      const panelSettings = bridgeSettings
      const effectiveModelId = (panelSettings?.modelId as string) || (typeof currentGenieState.modelId === 'string' ? currentGenieState.modelId : '') || activeTextModel?.id || ''
      if (!effectiveModelId) {
        toast.error('Please select a model before generating')
        if (mountedRef.current) setOrchestratorState({ status: 'error', message: 'No model selected' })
        return
      }

      const currentSettings = {
        modelId: effectiveModelId,
        modelFamilySlug: (panelSettings?.modelFamilySlug as string) || (typeof currentGenieState.modelFamilySlug === 'string' ? currentGenieState.modelFamilySlug : '') || (typeof currentGenieState.brandFamily === 'string' ? currentGenieState.brandFamily : '') || '',
        settings: (panelSettings?.settings as Record<string, unknown>) || (currentGenieState.settings as Record<string, unknown>) || {},
      }
      const currentOutputMode = (panelSettings?.outputMode as string) || 'in-canvas'

      onStateChange(prev => ({ ...prev, locked: true }))
      if (mountedRef.current) setOrchestratorState({ status: 'working', message: 'Generating text...', progress: 0, startedAt: new Date() })

      let placeholderIds: string[] = []
      try {
        const quantity = (currentSettings.settings?.iterations as number) || 1

        // 1. Create placeholder child nodes BEFORE generation (shows loading state)
        const bridgeStore = useCanvasBridgeStore.getState()
        const createPlaceholder = bridgeStore.createPlaceholderCallback
        const updateNodeState = bridgeStore.updateNodeStateCallback
        if (createPlaceholder) {
          placeholderIds = createPlaceholder({ parentNodeId: id, type: 'text', count: quantity })
        }

        // 2. Stream generation — one call per child node (sequential)
        let completedCount = 0
        const totalCount = placeholderIds.length
        let failureMessage = ''
        if (!totalCount) throw new Error('Could not create a text output on the canvas. Please retry.')

        for (let i = 0; i < totalCount; i++) {
          const childNodeId = placeholderIds[i]
          let childText = ''

          try {
            await generateTextStream(
              {
                prompt: promptString,
                systemPrompt: finalSystemPrompt || undefined,
                image: finalImage,
                model: currentSettings.modelId,
                quantity: 1,
                parameters: currentSettings.settings,
              },
              {
                onToken: (token) => {
                  childText += token
                  streamingStore.set(childNodeId, childText)
                  if (mountedRef.current) {
                    setOrchestratorState(prev => ({
                      ...prev,
                      message: totalCount > 1
                        ? `Generating ${i + 1}/${totalCount}... ${childText.length} chars`
                        : `Generating... ${childText.length} chars`,
                    }))
                  }
                },
                onProgress: (progress) => {
                  if (mountedRef.current) {
                    // Scale progress across all iterations
                    const iterationProgress = (completedCount + progress / 100) / totalCount * 100
                    setOrchestratorState(prev => ({ ...prev, progress: Math.round(iterationProgress) }))
                  }
                },
                onDone: (variant) => {
                  streamingStore.clear(childNodeId)
                  completedCount++
                  const item: CreatedItem = {
                    id: variant.id,
                    type: 'text' as const,
                    content: variant.text,
                    metadata: {
                      createdAt: new Date(variant.createdAt),
                      settings: { model: variant.model, provider: variant.provider },
                      isGenerated: true,
                      generatorNodeId: id,
                      generationSnapshot: {
                        modelId: currentSettings.modelId,
                        modelFamilySlug: currentSettings.modelFamilySlug,
                        settings: currentSettings.settings,
                        prompt: promptString,
                        ...(finalSystemPrompt && { systemPrompt: finalSystemPrompt }),
                      },
                    },
                  }
                  if (updateNodeState) {
                    updateNodeState(childNodeId, {
                      prompt: variant.text,
                      createdItems: [item],
                      generationStatus: 'complete',
                      generationProgress: 100,
                      generationError: undefined,
                    })
                  }
                },
                onError: (msg) => {
                  streamingStore.clear(childNodeId)
                  if (updateNodeState) {
                    updateNodeState(childNodeId, {
                      generationStatus: 'error',
                      generationProgress: 0,
                      generationError: msg,
                    })
                  }
                },
              }
            )
          } catch (iterErr) {
            // Mark this child as failed but continue with remaining iterations
            console.error(`Iteration ${i + 1}/${totalCount} failed:`, iterErr)
            failureMessage = iterErr instanceof Error ? iterErr.message : 'Text generation failed.'
            streamingStore.clear(childNodeId)
            if (updateNodeState) {
              updateNodeState(childNodeId, { generationStatus: 'error', generationProgress: 0, generationError: failureMessage })
            }
          }
        }

        // 3. Update parent node state (save model/settings, unlock)
        onStateChange(prev => ({
          ...prev,
          prompt: promptString,
          modelId: currentSettings.modelId,
          settings: currentSettings.settings,
          locked: false,
        }))

        if (mountedRef.current) {
          setOrchestratorState(completedCount === totalCount
            ? { status: 'success', message: 'Generated successfully', progress: 100 }
            : { status: 'error', message: completedCount ? `${completedCount} of ${totalCount} outputs completed. ${failureMessage}` : failureMessage, progress: Math.round(completedCount / totalCount * 100) })
        }
      } catch (err) {
        console.error('Generation failed:', err)
        // Clean up child placeholder nodes on error
        const bridgeStore = useCanvasBridgeStore.getState()
        const updateNodeState = bridgeStore.updateNodeStateCallback
        if (updateNodeState && placeholderIds.length > 0) {
          placeholderIds.forEach(pid => updateNodeState(pid, { generationStatus: 'error', generationProgress: 0, generationError: err instanceof Error ? err.message : 'Text generation failed.' }))
        }
        if (mountedRef.current) {
          onStateChange(prev => ({ ...prev, locked: false }))
          setOrchestratorState({ status: 'error', message: err instanceof Error ? err.message : 'Generation failed' })
        }
      }
    } finally {
      isGeneratingRef.current = false
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateTextStream, onStateChange, id, connectedPrompt, activeTextModel])

  // Programmatic action handler
  const action = genieState._action
  const actionTimestamp = action?.ts
  const handleGenerateRef = useRef(handleGenerate)
  handleGenerateRef.current = handleGenerate

  useEffect(() => {
    if (!action) return
    switch (action.type) {
      case 'generate': handleGenerateRef.current(); break
      case 'cancel': setOrchestratorState({ status: 'idle' }); break
      case 'clear': onStateChange(prev => ({ ...prev, createdItems: [] })); break
      case 'update':
        if (action.params?.prompt !== undefined) {
          currentPromptRef.current = action.params.prompt
          onStateChange(prev => ({ ...prev, prompt: action.params.prompt }))
        }
        break
    }
    onStateChange(prev => ({ ...prev, _action: null }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionTimestamp, action, onStateChange])

  // Pinned parameters support
  const pinnedNames = useMemo(() => {
    const raw = genieState.pinnedParameters
    return Array.isArray(raw) ? (raw as string[]) : []
  }, [genieState.pinnedParameters])

  const { parameters: pinnedParams, values: pinnedValues } = usePinnedParameters({
    pinnedNames,
    nodeType: 'text-llm',
    modelFamilySlug: (genieState.modelFamilySlug as string | undefined) || (genieState.brandFamily as string | undefined) || activeTextModel?.modelFamilySlug,
    modelId: (genieState.modelId as string | undefined) || activeTextModel?.id,
    purpose: 'create',
    settings: genieState.settings as Record<string, unknown> | undefined,
  })

  const handlePinnedParameterChange = useCallback((paramName: string, value: unknown) => {
    onStateChange(prev => ({
      ...prev,
      settings: {
        ...((prev.settings as Record<string, unknown>) || {}),
        [paramName]: value,
      },
    }))
  }, [onStateChange])

  // Model dropdown support for pinned model
  const pinnedModelsList = useMemo(() => {
    if (!pinnedNames.includes('__model__')) return undefined
    return textModels.map(m => ({ id: m.id, name: m.name }))
  }, [textModels, pinnedNames])

  const handlePinnedModelChange = useCallback((modelId: string) => {
    const model = findModelById(textModels, modelId)
    if (!model) return
    onStateChange(prev => ({
      ...prev,
      ...modelTitleState(prev, model.name),
      modelId: model.id,
      modelFamilySlug: model.modelFamilySlug,
    }))
  }, [textModels, onStateChange])

  const nodeProps = {
    id,
    selected: selected || genieState.selected,
    initialData: {
      ...genieState,
      ...(activeTextModel?.name ? modelTitleState(genieState, activeTextModel.name) : {}),
    },
    connectedPrompt,
    orchestratorState,
    onChange: handleChange,
    onStateChange,
    onSelect: handleSelect,
    onGenerate: handleGenerate,
    onHoverChange,
    loading,
    error,
    canvasId: (genieState.canvasId as string) || canvasId,
    costProvider: activeTextModel?.provider ?? null,
    costModel: activeTextModel?.id ?? null,
    walletBalance: walletBalance ?? undefined,
    pinnedParameters: pinnedParams.length > 0 ? pinnedParams : undefined,
    pinnedParameterValues: pinnedParams.length > 0 ? pinnedValues : undefined,
    onPinnedParameterChange: (pinnedParams.length > 0 || pinnedNames.includes('__model__')) ? handlePinnedParameterChange : undefined,
    pinnedModelName: pinnedNames.includes('__model__') ? (activeTextModel?.name || activeTextModel?.id || 'No model') : undefined,
    pinnedModels: pinnedModelsList,
    onPinnedModelChange: pinnedNames.includes('__model__') ? handlePinnedModelChange : undefined,
  }

  // Read handles from node data (set by toReactFlowNode or dynamic handle updates)
  const handles: HandleMeta[] = nodeData.handles || DEFAULT_TEXT_HANDLES

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <TextNode {...nodeProps} />
    </>
  )
}

export const TextNodeNode = React.memo(
  TextNodeNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as TextNodeNodeData
    const nextData = nextProps.data as unknown as TextNodeNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.titleSource === nextData.state.titleSource &&
      prevData.state.modelName === nextData.state.modelName &&
      prevData.state.prompt === nextData.state.prompt &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.state.locked === nextData.state.locked &&
      prevData.state.images === nextData.state.images &&
      prevData.state.createdItems === nextData.state.createdItems &&
      prevData.state._action === nextData.state._action &&
      prevData.state.pinnedParameters === nextData.state.pinnedParameters &&
      prevData.state.settings === nextData.state.settings &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)
TextNodeNode.displayName = 'TextNodeNode'
